import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import StatusBadge from "./StatusBadge";

interface PropertyDetailLayoutProps {
  images: string[];
  price: string;
  address: string;
  status?: "for-sale" | "sold" | "featured" | "new" | "investment" | "under-contract";
  beds?: number;
  baths?: number;
  sqft?: string;
  yearBuilt?: string;
  description: string;
  cta?: { text: string; href: string };
  variant?: "light" | "dark";
}

const PropertyDetailLayout = ({
  images,
  price,
  address,
  status,
  beds,
  baths,
  sqft,
  yearBuilt,
  description,
  cta,
  variant = "light",
}: PropertyDetailLayoutProps) => {
  const [activeImage, setActiveImage] = useState(0);
  const isDark = variant === "dark";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
      {/* Gallery */}
      <div className="space-y-3">
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          <img
            src={images[activeImage]}
            alt={address}
            className="w-full h-full object-cover"
          />
          {status && (
            <div className="absolute top-4 left-4">
              <StatusBadge variant={status} />
            </div>
          )}
        </div>
        {images.length > 1 && (
          <div className="grid grid-cols-4 gap-2">
            {images.slice(0, 4).map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                className={cn(
                  "aspect-[4/3] overflow-hidden border-2 transition-colors",
                  activeImage === i ? "border-accent" : "border-transparent hover:border-border"
                )}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Details */}
      <div className="space-y-6">
        <div>
          <p className={cn("font-headline text-2xl md:text-3xl mb-1", isDark ? "text-secondary-foreground" : "text-foreground")}>{price}</p>
          <p className={cn("text-sm", isDark ? "text-secondary-foreground/60" : "text-muted-foreground")}>{address}</p>
        </div>

        {/* Specs */}
        <div className={cn("flex flex-wrap gap-8 py-5 border-y", isDark ? "border-secondary-foreground/15" : "border-border")}>
          {beds !== undefined && (
            <div>
              <p className={cn("font-headline text-2xl", isDark ? "text-secondary-foreground" : "text-foreground")}>{beds}</p>
              <p className={cn("font-body text-xs uppercase tracking-widest", isDark ? "text-secondary-foreground/50" : "text-muted-foreground")}>Beds</p>
            </div>
          )}
          {baths !== undefined && (
            <div>
              <p className={cn("font-headline text-2xl", isDark ? "text-secondary-foreground" : "text-foreground")}>{baths}</p>
              <p className={cn("font-body text-xs uppercase tracking-widest", isDark ? "text-secondary-foreground/50" : "text-muted-foreground")}>Baths</p>
            </div>
          )}
          {sqft && (
            <div>
              <p className={cn("font-headline text-2xl", isDark ? "text-secondary-foreground" : "text-foreground")}>{sqft}</p>
              <p className={cn("font-body text-xs uppercase tracking-widest", isDark ? "text-secondary-foreground/50" : "text-muted-foreground")}>Sqft</p>
            </div>
          )}
          {yearBuilt && (
            <div>
              <p className={cn("font-headline text-2xl", isDark ? "text-secondary-foreground" : "text-foreground")}>{yearBuilt}</p>
              <p className={cn("font-body text-xs uppercase tracking-widest", isDark ? "text-secondary-foreground/50" : "text-muted-foreground")}>Year Built</p>
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <h3 className={cn("font-headline text-lg mb-3", isDark ? "text-secondary-foreground" : "text-foreground")}>About This Property</h3>
          <p className={cn("text-sm leading-relaxed", isDark ? "text-secondary-foreground/70" : "text-muted-foreground")}>{description}</p>
        </div>

        {/* CTA */}
        {cta && (
          <Button variant="gold" size="lg" asChild>
            <a href={cta.href}>{cta.text}</a>
          </Button>
        )}
      </div>
    </div>
  );
};

export default PropertyDetailLayout;
